import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, AlertCircle, Clock } from 'lucide-react';
import { SESSION_EXPIRED_FLAG } from '../lib/session';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Set when a login could not be renewed and the person was sent here.
  const [expired] = useState(() => {
    try {
      const was = sessionStorage.getItem(SESSION_EXPIRED_FLAG) === '1';
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
      return was;
    } catch (_) { return false; }
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    setLoading(false);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] relative overflow-hidden">
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1513331006937-db9db75ac1a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxuZXdzcGFwZXIlMjBwdWJsaXNoaW5nJTIwcHJpbnRpbmd8ZW58MHx8fHwxNzc0Njg4MjY2fDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.15)'
        }}
      />

      <div className="relative z-10 w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/publishos-logo.svg"
              alt="PublishOS"
              className="h-14"
              data-testid="login-brand-logo"
            />
          </div>
          <p className="text-[#94A3B8]">
            Media Operations Platform
          </p>
        </div>

        <div className="bg-[#121212] border border-[#1E293B] rounded-lg p-6">
          <h2 className="text-xl font-bold text-[#F8FAFC] mb-6 text-center" data-testid="auth-title">
            Sign In
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#94A3B8]">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] focus:ring-1 focus:ring-[#3B82F6] placeholder:text-[#475569]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#94A3B8]">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] focus:ring-1 focus:ring-[#3B82F6] placeholder:text-[#475569]"
              />
            </div>

            {expired && !error && (
              <div className="flex items-center gap-2 p-3 bg-amber-900/20 border border-amber-800 rounded-md text-amber-300 text-sm" data-testid="session-expired-message">
                <Clock className="w-4 h-4 shrink-0" />
                <span>Your session ended. Please sign in again.</span>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800 rounded-md text-red-400 text-sm" data-testid="auth-error-message">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white font-medium py-2"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
